import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { navigate } from 'gatsby';
import sortBy from 'lodash.sortby';
import uniqBy from 'lodash.uniqby';
import MediaQuery from 'react-responsive';
import { FilterBox, InputTextFuzzy, Icon } from '@massds/mayflower-react';
import { slugify, getObjByValue, getTextByValue } from '../../utils/index';
import { toSectionsURL } from '../../utils/nav';
import './index.scss';

class Filter extends Component {
  constructor(props) {
    super(props);
    const { path, filterData } = this.props;
    const filters = path.split('/');
    const [,, govLevelOne, govLevelTwo = '', govLevelThree = '', key = ''] = filters;
    this.state = {
      selected: [govLevelOne, govLevelTwo, govLevelThree, key].filter((level) => level).pop(),
      filterBoxExpanded: false
    };
    this.longestOrg = 0;
    this.longestItem = 0;
    this.fuzzyOptions = this.normalizeFuzzyOptions(filterData);
    this.orgOptions = this.normalizeOrgOptions(filterData);
  }

  toggleFilterBox = () => {
    this.setState((prevState) => ({ filterBoxExpanded: !prevState.filterBoxExpanded }));
  };

  buildNavigateUrl = (orgs) => {
    const { pathRoot } = this.props;
    const orgsArray = [];
    let i = 0;
    const orgsMax = orgs.length;
    for (; i < orgsMax; i += 1) {
      const org = orgs[i];
      if (org) {
        orgsArray.push(slugify(org));
      }
    }
    const orgsPath = orgsArray.join('/');
    const url = `${pathRoot}${orgsPath}`;
    navigate(url);
  };


  createFilterOptions = (data, key, displayKey, abbrKey) => {
    const sortedData = sortBy(data, key);
    const filterOptions = [];
    let i = 0;
    const sortMax = sortedData.length;
    for (; i < sortMax; i += 1) {
      const org = sortedData[i];
      const { govLevelOne, govLevelThree, govLevelTwo } = org;
      // fall back on short name if display name doesn't exist
      const displayText = org[displayKey] || org[key];
      // treat 'NON' and acronyms with digits as invalid acronyms
      const abbrText = (org[abbrKey] !== 'NON' && !(/[0-9]/g).test(org[abbrKey])) && org[abbrKey];
      if (org[key] && org[key] !== 'undefined') {
        const orgText = abbrText ? `${displayText} (${abbrText})` : displayText;
        this.longestOrg = this.longestOrg > orgText.length ? this.longestOrg : orgText.length;
        filterOptions.push({
          // if acronym exists, append it to the display text
          text: orgText,
          key: slugify(org[key]),
          level: key,
          govLevelOne,
          govLevelThree,
          govLevelTwo
        });
      }
    }
    return filterOptions;
  };


  normalizeFuzzyOptions = (data) => {
    const items = [];
    let i = 0;
    const rawDataMax = data.length;
    for (; i < rawDataMax; i += 1) {
      const d = data[i];
      const {
        account, description, govLevelTwo, govLevelOne, govLevelThree
      } = d;
      const item = {
        key: account,
        description,
        govLevelTwo,
        govLevelOne,
        govLevelThree,
        text: `${account} - ${description}`
      };
      this.longestItem = this.longestItem > item.text.length ? this.longestItem : item.text.length;
      items.push(item);
    }
    return items;
  };


  normalizeOrgOptions = (data) => {
    const level1Options = uniqBy(this.createFilterOptions(data, 'govLevelOne', 'govLevelOneDisplay', 'govLevelOneAbbr'), 'key');
    const level2Options = uniqBy(this.createFilterOptions(data, 'govLevelTwo', 'govLevelTwoDisplay', 'govLevelTwoAbbr'), 'key');
    const level3Options = uniqBy(this.createFilterOptions(data, 'govLevelThree', 'govLevelThreeDisplay', 'govLevelOneThreeAbbr'), 'key');
    return[{ text: 'Statewide', key: 'all', level: 'govLevelZero' }, ...level1Options, ...level2Options, ...level3Options];
  };

  render() {
    const {
      pathRoot, fuzzyDisplay, orgDisplay
    } = this.props;
    const { selected, filterBoxExpanded } = this.state;
    const orgProps = {
      ...orgDisplay,
      options: this.orgOptions,
      selected: getTextByValue(this.orgOptions, selected),
      onSuggestionClick: (e, { suggestion }) => {
        e.preventDefault();
        const item = getObjByValue(this.orgOptions, suggestion.item.text, 'text') || {};
        const {
          level, govLevelOne, govLevelTwo, govLevelThree
        } = item;
        // prevent navigate on filter clear
        if (level) {
          switch (level) {
            case 'govLevelOne':
              this.buildNavigateUrl([govLevelOne]);
              break;
            case 'govLevelTwo':
              this.buildNavigateUrl([govLevelOne, govLevelTwo]);
              break;
            case 'govLevelThree':
              this.buildNavigateUrl([govLevelOne, govLevelTwo, govLevelThree]);
              break;
            default:
              this.buildNavigateUrl([]);
          }
        }
      },
      keys: ['text'],
      fuseOptions: {
        shouldSort: true,
        findAllMatches: true,
        includeMatches: true,
        threshold: 0.3,
        location: 0,
        distance: 300,
        minMatchCharLength: 1,
        maxPatternLength: this.longestOrg + 20
      }
    };

    const fuzzyProps = {
      ...fuzzyDisplay,
      options: this.fuzzyOptions,
      selected: getTextByValue(this.fuzzyOptions, selected),
      onSuggestionClick: (e, { suggestion }) => {
        e.preventDefault();
        const item = getObjByValue(this.fuzzyOptions, suggestion.item.text, 'text');
        const slug = toSectionsURL({
          href: pathRoot,
          sections: [item.govLevelOne, item.govLevelTwo, item.govLevelThree, item.key]
        });
        if (suggestion) {
          navigate(slug);
        }
      },
      keys: ['text'],
      fuseOptions: {
        shouldSort: true,
        findAllMatches: true,
        includeMatches: true,
        threshold: 0.2,
        location: 0,
        distance: 400,
        minMatchCharLength: 1,
        maxPatternLength: this.longestItem + 20
      }
    };

    const filterProps = {
      fields: [
        {
          class: 'ma__filter-box',
          component: <InputTextFuzzy {...orgProps} />
        },
        {
          class: 'ma__filter-box ma__filter-box--search',
          component: <InputTextFuzzy {...fuzzyProps} />
        }
      ],
      submitButton: null,
      clearButton: null,
      filterLabel: `${orgDisplay.label} or ${fuzzyDisplay.label}`,
      filterNote: "As you select an option, you'll be automatically redirected to the page you select."
    };
    const toggleButtonClasses = classNames({
      'ma__search-banner__filter-box-toggle': true,
      'ma__search-banner__filter-box-toggle--expanded': filterBoxExpanded
    });
    const filterToggleText = filterBoxExpanded ? 'Hide Search Options' : 'Search by Organization or Line Item';
    return(
      <MediaQuery minDeviceWidth={910}>
        {
          (matches) => {
            if (!matches) {
              return(
                <Fragment>
                  <div className="main-content--two ma__search-banner__filter-box-toggle-container">
                    <button
                      onClick={this.toggleFilterBox}
                      type="button"
                      className={toggleButtonClasses}
                      aria-expanded={filterBoxExpanded}
                    >
                      {filterToggleText}
                      <Icon name="chevron" svgWidth={20} svgHeight={20} />
                    </button>
                  </div>
                  {filterBoxExpanded && <FilterBox {...filterProps} />}
                </Fragment>
              );
            }
            return(<FilterBox {...filterProps} />);
          }
        }
      </MediaQuery>
    );
  }
}

Filter.propTypes = {
  /* Relative path passed from the page/template excluding path prefix.
    This is used for setting the filter selected state and populate the selected option in the org InputTextFuzzy filter.
  */
  path: PropTypes.string,
  /* Root slug of the result pages, passed from HOC Filter component. e.g.
    From govbudget AppropriationFilter: '/appropriations/' (line item pages root slug)
    From capital PlanFilter: '/beneficiary-agency/' (plan item pages root slug)
    This is used for building the navigation url for both InputTextFuzzy filters' options.
  */
  pathRoot: PropTypes.string,
  /* Data array of lineItem/planItem object.
    This is used for creating the options data for both InputTextFuzzy filters. See normalizeFuzzyOptions and normalizeOrgOptions functions.
  */
  filterData: PropTypes.array,
  /* Display options for the line item / plan item search. */
  fuzzyDisplay: PropTypes.shape({
    /** The id of the typeahead element. */
    id: PropTypes.string,
    /** The label text above the input text box. */
    label: PropTypes.string,
    /** The placeholder text to appear in the input text box. */
    placeholder: PropTypes.string,
    /** The id of the the input tag */
    inputId: PropTypes.string
  }),
  /* Display options for the org search. */
  orgDisplay: PropTypes.shape({
    /** The id of the typeahead element. */
    id: PropTypes.string,
    /** The label text above the input text box. */
    label: PropTypes.string,
    /** The placeholder text to appear in the input text box. */
    placeholder: PropTypes.string,
    /** The id of the the input tag */
    inputId: PropTypes.string
  })
};

export default Filter;
